class CustomDateInput {
  static DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];
  static MONTHS = ['January','February','March','April','May','June',
                   'July','August','September','October','November','December'];

  constructor(containerSelector, options = {}) {
    this.container = typeof containerSelector === 'string'
      ? document.querySelector(containerSelector)
      : containerSelector;

    if (!this.container) throw new Error(`CustomDateInput: container not found → ${containerSelector}`);

    this.options = {
      name:        'date',
      label:       '',
      placeholder: 'DD/MM/YYYY',
      required:    false,
      min:         null,   // 'YYYY-MM-DD'
      max:         null,   // 'YYYY-MM-DD'
      onChange:    null,
      ...options,
    };

    // State
    this._value       = '';
    this._selectedDate = null;
    this._viewYear    = new Date().getFullYear();
    this._viewMonth   = new Date().getMonth();
    this._open        = false;

    this._build();
    this._bindEvents();
  }

  // ─── Helpers ────────────────────────────────────────────────────────────────

  _pad(n) { return String(n).padStart(2, '0'); }

  _parseDate(str) {
    const m = str.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return null;
    const [, dd, mm, yyyy] = m;
    const d = new Date(+yyyy, +mm - 1, +dd);
    if (isNaN(d) || d.getDate() !== +dd || d.getMonth()+1 !== +mm || d.getFullYear() !== +yyyy) return null;
    return d;
  }

  _todayMidnight() {
    const t = new Date();
    return new Date(t.getFullYear(), t.getMonth(), t.getDate());
  }

  _effectiveMin() {
    const today = this._todayMidnight();
    if (!this.options.min) return today;
    const m = new Date(this.options.min);
    return m > today ? m : today;
  }

  _isDisabled(year, month, day) {
    const d = new Date(year, month, day);
    if (d < this._effectiveMin()) return true;
    if (this.options.max && d > new Date(this.options.max)) return true;
    return false;
  }

  _validate(str) {
    if (!str && this.options.required) return '';
    if (!str) return '';
    const date = this._parseDate(str);
    if (!date) return '';
    if (date < this._effectiveMin()) return '';
    if (this.options.max && date > new Date(this.options.max))
      return ``;
    return '';
  }

  // ─── Build DOM ───────────────────────────────────────────────────────────────

  _build() {
    this.container.style.position = 'relative';
    this.container.innerHTML = '';

    // Label
    if (this.options.label) {
      this._labelEl = document.createElement('label');
      this._labelEl.setAttribute('for', this.options.name);
      this._labelEl.className = 'cdi-label';
      this._labelEl.innerHTML = this.options.label +
        (this.options.required ? ' <span class="cdi-required">*</span>' : '');
      this.container.appendChild(this._labelEl);
    }

    // Input row
    this._rowEl = document.createElement('div');
    this._rowEl.className = 'cdi-row';

    this._inputEl = document.createElement('input');
    this._inputEl.type        = 'text';
    this._inputEl.id          = this.options.name;
    this._inputEl.name        = this.options.name;
    this._inputEl.inputMode   = 'numeric';
    this._inputEl.placeholder = this.options.placeholder;
    this._inputEl.autocomplete = 'off';
    this._inputEl.className   = 'cdi-input';
    if (this.options.required) this._inputEl.required = true;

    this._iconBtn = document.createElement('button');
    this._iconBtn.type      = 'button';
    this._iconBtn.className = 'cdi-icon-btn';
    this._iconBtn.setAttribute('aria-label', 'Open calendar');
    this._iconBtn.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
        <path d="M1.50646 15.9294C1.08549 15.9294 0.729167 15.7835 0.4375 15.4919C0.145833 15.2002 0 14.8439 0 14.4229V3.26917C0 2.84819 0.145833 2.49187 0.4375 2.20021C0.729167 1.90854 1.08549 1.76271 1.50646 1.76271H2.66021V0.640834C2.66021 0.458195 2.72139 0.305694 2.84375 0.183333C2.96611 0.0611109 3.11861 0 3.30125 0C3.48403 0 3.63653 0.0611109 3.75875 0.183333C3.88111 0.305694 3.94229 0.458195 3.94229 0.640834V1.76271H10.2565V0.625C10.2565 0.447639 10.3162 0.299097 10.4358 0.179375C10.5556 0.0597914 10.7041 0 10.8815 0C11.0588 0 11.2073 0.0597914 11.3269 0.179375C11.4466 0.299097 11.5065 0.447639 11.5065 0.625V1.76271H12.6602C13.0812 1.76271 13.4375 1.90854 13.7292 2.20021C14.0208 2.49187 14.1667 2.84819 14.1667 3.26917V14.4229C14.1667 14.8439 14.0208 15.2002 13.7292 15.4919C13.4375 15.7835 13.0812 15.9294 12.6602 15.9294H1.50646ZM1.50646 14.6794H12.6602C12.7244 14.6794 12.7831 14.6526 12.8365 14.5992C12.8899 14.5458 12.9167 14.4871 12.9167 14.4229V6.6025H1.25V14.4229C1.25 14.4871 1.27674 14.5458 1.33021 14.5992C1.38354 14.6526 1.44229 14.6794 1.50646 14.6794ZM1.25 5.3525H12.9167V3.26917C12.9167 3.205 12.8899 3.14625 12.8365 3.09292C12.7831 3.03944 12.7244 3.01271 12.6602 3.01271H1.50646C1.44229 3.01271 1.38354 3.03944 1.33021 3.09292C1.27674 3.14625 1.25 3.205 1.25 3.26917V5.3525Z" fill="#000933"/>
    </svg>`;

    this._rowEl.appendChild(this._inputEl);
    this._rowEl.appendChild(this._iconBtn);
    this.container.appendChild(this._rowEl);

    // Error
    this._errorEl = document.createElement('p');
    this._errorEl.className = 'cdi-error';
    this._errorEl.setAttribute('role', 'alert');
    this._errorEl.style.display = 'none';
    this.container.appendChild(this._errorEl);

    // Calendar popup
    this._calEl = document.createElement('div');
    this._calEl.className = 'cdi-calendar';
    this._calEl.style.display = 'none';
    this.container.appendChild(this._calEl);

    this._injectStyles();
  }

  _renderCalendar() {
    const today  = new Date();
    const year   = this._viewYear;
    const month  = this._viewMonth;

    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDay    = new Date(year, month, 1).getDay();

    let html = `
      <div class="cdi-cal-header">
        <button type="button" class="cdi-nav-btn" data-action="prev">&#8249;</button>
        <span class="cdi-month-year">${CustomDateInput.MONTHS[month]} ${year}</span>
        <button type="button" class="cdi-nav-btn" data-action="next">&#8250;</button>
      </div>
      <div class="cdi-grid">
        ${CustomDateInput.DAYS.map(d => `<div class="cdi-day-name">${d}</div>`).join('')}
    `;

    // Empty cells
    for (let i = 0; i < firstDay; i++) html += `<div></div>`;

    // Day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const disabled = this._isDisabled(year, month, day);
      const isSelected = this._selectedDate &&
        this._selectedDate.getDate() === day &&
        this._selectedDate.getMonth() === month &&
        this._selectedDate.getFullYear() === year;
      const isToday = today.getDate() === day &&
        today.getMonth() === month &&
        today.getFullYear() === year;

      let cls = 'cdi-day-btn';
      if (disabled)   cls += ' cdi-disabled';
      if (isSelected) cls += ' cdi-selected';
      else if (isToday) cls += ' cdi-today';

      html += `<button type="button" class="${cls}" data-day="${day}" ${disabled ? 'disabled' : ''}>${day}</button>`;
    }

    html += `</div>
      <div class="cdi-cal-footer">
        <button type="button" class="cdi-today-btn" data-action="today">Today</button>
        <button type="button" class="cdi-clear-btn" data-action="clear">Clear</button>
      </div>`;

    this._calEl.innerHTML = html;
  }

  // ─── Events ──────────────────────────────────────────────────────────────────

  _bindEvents() {
    // Input typing
    this._onInput = (e) => {
      const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
      let fmt = digits;
      if (digits.length > 2) fmt = digits.slice(0,2) + '/' + digits.slice(2);
      if (digits.length > 4) fmt = digits.slice(0,2) + '/' + digits.slice(2,4) + '/' + digits.slice(4);
      this._inputEl.value = fmt;
      this._value = fmt;
      if (fmt.length === 10) {
        const err = this._validate(fmt);
        this._showError(err);
        const date = err ? null : this._parseDate(fmt);
        this._selectedDate = date;
        this.options.onChange?.(fmt, date);
      } else {
        this._showError('');
        this._selectedDate = null;
        this.options.onChange?.(fmt, null);
      }
    };

    // Backspace over slash
    this._onKeyDown = (e) => {
      if (e.key === 'Backspace') {
        const pos = this._inputEl.selectionStart;
        if (pos && this._inputEl.value[pos - 1] === '/') {
          e.preventDefault();
          const val    = this._inputEl.value;
          const newVal = val.slice(0, pos - 2) + val.slice(pos);
          const digits = newVal.replace(/\D/g, '').slice(0, 8);
          let fmt = digits;
          if (digits.length > 2) fmt = digits.slice(0,2) + '/' + digits.slice(2);
          if (digits.length > 4) fmt = digits.slice(0,2) + '/' + digits.slice(2,4) + '/' + digits.slice(4);
          this._inputEl.value = fmt;
          this._value = fmt;
          requestAnimationFrame(() => this._inputEl.setSelectionRange(pos - 2, pos - 2));
        }
      }
      if (e.key === 'Escape') this._closeCalendar();
    };

    // Focus / blur
    this._onFocus = () => this._rowEl.classList.add('cdi-focused');
    this._onBlur  = (e) => {
      if (this.container.contains(e.relatedTarget)) return;
      this._rowEl.classList.remove('cdi-focused');
      this._showError(this._validate(this._value));
    };

    // Calendar icon toggle
    this._onIconDown = (e) => {
      e.preventDefault();
      this._open ? this._closeCalendar() : this._openCalendar();
    };

    // Calendar clicks (event delegation)
    this._onCalClick = (e) => {
      const btn = e.target.closest('button[data-action], button[data-day]');
      if (!btn) return;
      const action = btn.dataset.action;
      const day    = btn.dataset.day;

      if (action === 'prev') {
        if (this._viewMonth === 0) { this._viewMonth = 11; this._viewYear--; }
        else this._viewMonth--;
        this._renderCalendar();
      } else if (action === 'next') {
        if (this._viewMonth === 11) { this._viewMonth = 0; this._viewYear++; }
        else this._viewMonth++;
        this._renderCalendar();
      } else if (action === 'today') {
        const t = new Date();
        this._viewYear = t.getFullYear();
        this._viewMonth = t.getMonth();
        this._selectDate(t);
      } else if (action === 'clear') {
        this._reset();
      } else if (day) {
        this._selectDate(new Date(this._viewYear, this._viewMonth, +day));
      }
    };

    // Outside click closes calendar
    this._onDocClick = (e) => {
      if (!this.container.contains(e.target)) this._closeCalendar();
    };

    this._inputEl.addEventListener('input',   this._onInput);
    this._inputEl.addEventListener('keydown', this._onKeyDown);
    this._inputEl.addEventListener('focus',   this._onFocus);
    this._inputEl.addEventListener('blur',    this._onBlur);
    this._iconBtn.addEventListener('mousedown', this._onIconDown);
    this._calEl.addEventListener('click', this._onCalClick);
    document.addEventListener('mousedown', this._onDocClick);
  }

  // ─── Calendar open/close ─────────────────────────────────────────────────────

  _openCalendar() {
    // Sync view to selected date or today
    const ref = this._selectedDate || new Date();
    this._viewYear  = ref.getFullYear();
    this._viewMonth = ref.getMonth();
    this._renderCalendar();
    this._calEl.style.display = 'block';
    this._iconBtn.classList.add('cdi-icon-active');
    this._open = true;
  }

  _closeCalendar() {
    this._calEl.style.display = 'none';
    this._iconBtn.classList.remove('cdi-icon-active');
    this._open = false;
  }

  _selectDate(date) {
    const fmt = `${this._pad(date.getDate())}/${this._pad(date.getMonth()+1)}/${date.getFullYear()}`;
    this._inputEl.value = fmt;
    this._value         = fmt;
    this._selectedDate  = date;
    this._showError('');
    this.options.onChange?.(fmt, date);
    this._closeCalendar();
    this._inputEl.focus();
  }

  _reset() {
    this._inputEl.value = '';
    this._value         = '';
    this._selectedDate  = null;
    this._showError('');
    this.options.onChange?.('', null);
    this._closeCalendar();
  }

  _showError(msg) {
    if (msg) {
      this._errorEl.textContent = msg;
      this._errorEl.style.display = 'block';
      this._rowEl.classList.add('cdi-error-state');
    } else {
      this._errorEl.style.display = 'none';
      this._rowEl.classList.remove('cdi-error-state');
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  getValue()         { return this._value; }
  getDate()          { return this._selectedDate; }
  setValue(str)      { this._inputEl.value = str; this._onInput({ target: this._inputEl }); }
  reset()            { this._reset(); }
  destroy() {
    this._inputEl.removeEventListener('input',     this._onInput);
    this._inputEl.removeEventListener('keydown',   this._onKeyDown);
    this._inputEl.removeEventListener('focus',     this._onFocus);
    this._inputEl.removeEventListener('blur',      this._onBlur);
    this._iconBtn.removeEventListener('mousedown', this._onIconDown);
    this._calEl.removeEventListener('click',       this._onCalClick);
    document.removeEventListener('mousedown',      this._onDocClick);
    this.container.innerHTML = '';
  }

  // ─── Styles (injected once) ──────────────────────────────────────────────────

  _injectStyles() {
    if (document.getElementById('cdi-styles')) return;
    const style = document.createElement('style');
    style.id = 'cdi-styles';
    style.textContent = `
      @keyframes cdiCalIn {
        from { opacity:0; transform:translateY(-8px) scale(0.97); }
        to   { opacity:1; transform:translateY(0)    scale(1);    }
      }

      .cdi-label {
        display: block;
        font-size: 13px; font-weight: 600;
        color: #374151; letter-spacing: 0.02em;
        margin-bottom: 6px;
      }
      .cdi-required { color: #ef4444; }

      .cdi-row {
        display: flex; align-items: center;
        border: 1px solid #C1CFD7;
        overflow: hidden;
        transition: border-color .15s, box-shadow .15s;
      }
      .cdi-row.cdi-focused    { border-color: #C1CFD7; }

      .cdi-input {
        flex: 1; border: none !important; outline: none;
        padding: 0 12px; font-size: 14px;
        height: 40px;
        color: #111827; background: transparent;
      }

      .cdi-icon-btn {
        display: flex; align-items: center; justify-content: center;
        width: 40px; height: 40px; flex-shrink: 0;
        border: none;
        background: transparent;;
        cursor: pointer;
        transition: background .15s, color .15s;
      }
      .cdi-icon-btn.cdi-icon-active,
      .cdi-icon-btn:hover { background: #eff6ff; color: #2563eb; }

      .cdi-error {
        font-size: 12px; color: #ef4444; margin: 4px 0 0;
      }

      /* ── Calendar ── */
      .cdi-calendar {
        position: absolute; top: calc(100% + 8px); left: 0; z-index: 1000;
        background: #fff; border-radius: 16px;
        box-shadow: 0 12px 40px rgba(0,0,0,.15);
        padding: 16px; width: 288px;
        user-select: none;
        animation: cdiCalIn .15s ease;
      }

      .cdi-cal-header {
        display: flex; align-items: center;
        justify-content: space-between; margin-bottom: 14px;
      }
      .cdi-nav-btn {
        background: #f3f4f6; border: none; cursor: pointer;
        font-size: 20px; color: #374151;
        width: 32px; height: 32px; border-radius: 8px;
        display: flex; align-items: center; justify-content: center;
        line-height: 1; transition: background .1s;
      }
      .cdi-nav-btn:hover { background: #e5e7eb; }
      .cdi-month-year { font-size: 15px; font-weight: 700; color: #111827; }

      .cdi-grid {
        display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px;
      }
      .cdi-day-name {
        text-align: center; font-size: 11px; font-weight: 600;
        color: #9ca3af; padding: 4px 0 10px;
      }
      .cdi-day-btn {
        border: none; background: none; cursor: pointer;
        border-radius: 8px; padding: 7px 2px; font-size: 13px;
        font-weight: 500; color: #374151; text-align: center;
        width: 100%; transition: background .1s, color .1s;
        font-family: inherit;
      }
      .cdi-day-btn:not(.cdi-disabled):not(.cdi-selected):hover { background: #f3f4f6; }
      .cdi-day-btn.cdi-selected { background: #2563eb; color: #fff; font-weight: 700; }
      .cdi-day-btn.cdi-today    { background: #eff6ff; color: #2563eb; font-weight: 700; }
      .cdi-day-btn.cdi-disabled { color: #d1d5db; cursor: not-allowed; opacity: .5; }

      .cdi-cal-footer {
        display: flex; justify-content: space-between;
        margin-top: 12px; padding-top: 12px;
        border-top: 1px solid #f3f4f6;
      }
      .cdi-today-btn {
        background: none; border: none; cursor: pointer;
        font-size: 13px; color: #2563eb; font-weight: 600;
        padding: 4px 8px; border-radius: 6px; font-family: inherit;
      }
      .cdi-clear-btn {
        background: none; border: none; cursor: pointer;
        font-size: 13px; color: #9ca3af; font-weight: 500;
        padding: 4px 8px; border-radius: 6px; font-family: inherit;
      }
      .cdi-today-btn:hover { background: #eff6ff; }
      .cdi-clear-btn:hover  { background: #f3f4f6; }
    `;
    document.head.appendChild(style);
  }
}
